import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Loader2,
  Download,
  AlertCircle,
  Landmark,
  Building2,
  Database,
  Calendar,
  Layers,
  Filter,
  Check,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import DataSourceNotice from "@/components/DataSourceNotice";

import KPIDashboard from "@/components/financial/KPIDashboard";
import IncomeStatement from "@/components/financial/IncomeStatement";
import BalanceSheet from "@/components/financial/BalanceSheet";
import CashFlowForecast from "@/components/financial/CashFlowForecast";
import TrialBalance from "@/components/financial/TrialBalance";
import FinancialChat from "@/components/financial/FinancialChat";
import DailyCollections from "@/components/financial/DailyCollections";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const now = new Date();
const defaultFY = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const fyOptions = Array.from({ length: 5 }, (_, i) => defaultFY - i);

const PERIODS = Array.from({ length: 12 }, (_, i) => {
  const p = i + 1;
  const month = ((p - 1 + 3) % 12) + 1;
  const monthName = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ][month - 1];
  return { value: String(p), label: `${String(p).padStart(2, "0")} - ${monthName}` };
});

const getMonthDateRange = (fy, pNum) => {
  const p = Number(pNum) || 1;
  const year = p <= 9 ? Number(fy) : Number(fy) + 1;
  const month = ((p - 1 + 3) % 12) + 1;
  const lastDay = new Date(year, month, 0).getDate();
  const from = `${year}-${String(month).padStart(2, "0")}-01`;
  const to = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { from, to };
};

const DEFAULT_PROFIT_CENTERS = {
  "1000": [
    { profit_center: "1100", name: "Doddaballapura", co_area: "A000" },
    { profit_center: "1200", name: "Maluru-1", co_area: "A000" },
    { profit_center: "1300", name: "Maluru-2", co_area: "A000" },
    { profit_center: "1400", name: "Morbi-w", co_area: "A000" },
  ],
  "2000": [
    { profit_center: "2100", name: "Parayil Plant 1", co_area: "A000" },
    { profit_center: "2200", name: "Parayil Processing", co_area: "A000" },
    { profit_center: "2300", name: "Parayil Logistics", co_area: "A000" },
  ],
};

const DEFAULT_SEGMENTS = {
  "1000": [
    { segment: "1000_A", name: "Segment A" },
    { segment: "1000_B", name: "Segment B" },
    { segment: "1000_C", name: "Segment C" },
    { segment: "KMBL", name: "SEGMENT_KMBL" },
  ],
  "2000": [
    { segment: "2000_A", name: "Food & Agro" },
    { segment: "2000_B", name: "Exports" },
    { segment: "PRYL", name: "SEGMENT_PARAYIL" },
  ],
};

const FinancialAnalyst = () => {
  const [exporting, setExporting] = useState(false);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");

  // ---- DASHBOARD TAB (Defaults pre-populated, auto-loads on mount) ----
  const [dashCompanyCode, setDashCompanyCode] = useState("1000");
  const [dashFiscalYear, setDashFiscalYear] = useState(String(defaultFY));
  const [dashStatementVersion, setDashStatementVersion] = useState("1000");
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);

  // ---- STATEMENTS & TRIAL BALANCE TABS (Independent per-tab state) ----
  const createDefaultTabState = () => ({
    companyCode: "",
    statementVersion: "",
    fiscalYear: "",
    periodType: "",
    fromDate: "",
    toDate: "",
    tbFromDate: "",
    tbToDate: "",
    ledger: "0L",
    periodNumber: "",
    comparisonYear: "",
    comparisonPeriodNumber: "",
    viewMode: "standard",
    profitCenter: "",
    includeProfitCenter: false,
    segment: "",
    currencyType: "local",
    statementType: "normal",
    data: null,
    compData: null,
    loading: false,
  });

  const [tabStates, setTabStates] = useState({
    income: createDefaultTabState(),
    balance: createDefaultTabState(),
    "trial-balance": createDefaultTabState(),
    cashflow: createDefaultTabState(),
  });

  const [statementVersions, setStatementVersions] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [segments, setSegments] = useState([]);
  const [isAdaptFiltersOpen, setIsAdaptFiltersOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Active tab's current state slice
  const currentTabState = useMemo(() => {
    return tabStates[activeTab] || createDefaultTabState();
  }, [tabStates, activeTab]);

  // Helper to update fields on the active tab ONLY
  const updateCurrentTab = (updates) => {
    setTabStates((prev) => {
      const current = prev[activeTab] || createDefaultTabState();
      const patch = typeof updates === "function" ? updates(current) : updates;
      return {
        ...prev,
        [activeTab]: {
          ...current,
          ...patch,
        },
      };
    });
  };

  const handleCompanyChange = (val) => {
    updateCurrentTab({
      companyCode: val,
      statementVersion: "",
      profitCenter: "",
      segment: "",
      data: null,
      compData: null,
    });
  };

  const handleFiscalYearChange = (val) => {
    const fy = parseInt(val, 10);
    if (!isNaN(fy)) {
      updateCurrentTab({
        fiscalYear: val,
        fromDate: `${fy}-04-01`,
        toDate: `${fy + 1}-03-31`,
        tbFromDate: `${fy}-04-01`,
        tbToDate: `${fy + 1}-03-31`,
        comparisonYear: String(fy - 1),
      });
    } else {
      updateCurrentTab({
        fiscalYear: val,
        fromDate: "",
        toDate: "",
        tbFromDate: "",
        tbToDate: "",
        comparisonYear: "",
      });
    }
  };

  const handlePeriodChange = (val) => {
    const current = tabStates[activeTab] || createDefaultTabState();
    let newFrom = current.fromDate;
    let newTo = current.toDate;
    if (current.fiscalYear && val && val !== "all") {
      const range = getMonthDateRange(current.fiscalYear, val);
      newFrom = range.from;
      newTo = range.to;
    }
    updateCurrentTab({
      periodNumber: val === "all" ? "" : val,
      fromDate: newFrom,
      toDate: newTo,
    });
  };

  const handleViewModeChange = (mode) => {
    const current = tabStates[activeTab] || createDefaultTabState();
    if (mode === "comparison") {
      const compPer = (!current.comparisonPeriodNumber || current.comparisonPeriodNumber === "none") ? (current.periodNumber || "1") : current.comparisonPeriodNumber;
      updateCurrentTab({
        viewMode: mode,
        comparisonPeriodNumber: compPer,
      });
    } else {
      updateCurrentTab({
        viewMode: mode,
        comparisonPeriodNumber: "",
      });
    }
  };

  const handleCompPeriodChange = (val) => {
    if (val === "none" || !val) {
      updateCurrentTab({
        comparisonPeriodNumber: "",
        viewMode: "standard",
      });
    } else {
      updateCurrentTab({
        comparisonPeriodNumber: val,
        viewMode: "comparison",
      });
    }
  };

  const activeAdaptFilterCount = useMemo(() => {
    const cur = tabStates[activeTab] || createDefaultTabState();
    let count = 0;
    if (cur.ledger && cur.ledger !== "0L") count++;
    if (cur.profitCenter && cur.profitCenter.trim()) count++;
    if (cur.includeProfitCenter) count++;
    if (cur.segment && cur.segment.trim()) count++;
    if (cur.currencyType !== "local") count++;
    if (cur.statementType !== "normal") count++;
    return count;
  }, [tabStates, activeTab]);

  // Fetch company codes on mount
  useEffect(() => {
    const fetchCodes = async () => {
      setLoadingCodes(true);
      try {
        const codesRes = await axios.get(`${API}/financial-agent/company-codes`);
        setCompanyCodes(codesRes.data?.company_codes || []);
      } catch {
        setCompanyCodes([]);
      } finally {
        setLoadingCodes(false);
      }
    };
    fetchCodes();
  }, []);

  // Fetch company-specific metadata (Statement Versions, Profit Centers, Segments) dynamically when active tab's companyCode changes
  useEffect(() => {
    const code = (tabStates[activeTab]?.companyCode || "").trim();
    if (!code) {
      setStatementVersions([]);
      setProfitCenters([]);
      setSegments([]);
      return;
    }
    const fetchCompanyMetadata = async () => {
      try {
        const [versionsRes, pcRes, segRes] = await Promise.all([
          axios.get(`${API}/financial-agent/statement-versions`, { params: { company_code: code } }),
          axios.get(`${API}/financial-agent/profit-centers`, { params: { company_code: code } }),
          axios.get(`${API}/financial-agent/segments`, { params: { company_code: code } }),
        ]);
        const vers = versionsRes.data?.statement_versions || [];
        setStatementVersions(vers);
        const pcs = pcRes.data?.profit_centers;
        setProfitCenters(pcs && pcs.length > 0 ? pcs : (DEFAULT_PROFIT_CENTERS[code] || DEFAULT_PROFIT_CENTERS["1000"]));
        const segs = segRes.data?.segments;
        setSegments(segs && segs.length > 0 ? segs : (DEFAULT_SEGMENTS[code] || DEFAULT_SEGMENTS["1000"]));
      } catch (err) {
        console.warn("Failed fetching company metadata", err);
        setProfitCenters(DEFAULT_PROFIT_CENTERS[code] || DEFAULT_PROFIT_CENTERS["1000"]);
        setSegments(DEFAULT_SEGMENTS[code] || DEFAULT_SEGMENTS["1000"]);
      }
    };
    fetchCompanyMetadata();
  }, [activeTab, tabStates[activeTab]?.companyCode]);

  // Auto-fetch Dashboard KPI data on initial mount ONLY
  const fetchDashboardData = useCallback(async (code = dashCompanyCode, fy = dashFiscalYear, fsv = dashStatementVersion) => {
    if (!code || !code.trim()) return;
    setDashLoading(true);
    try {
      const res = await axios.get(`${API}/financial-agent/data`, {
        params: {
          fiscal_year: fy ? Number(fy) : undefined,
          period_type: "monthly",
          company_code: code.trim(),
          statement_version: fsv || undefined,
        },
      });
      setDashData(res.data);
    } catch (e) {
      console.error("Dashboard fetch error:", e);
      if (e.response?.status === 403) {
        toast.error("Access denied. Financial Analyst role required.");
      } else {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setDashLoading(false);
    }
  }, [dashCompanyCode, dashFiscalYear, dashStatementVersion]);

  useEffect(() => {
    fetchDashboardData("1000", String(defaultFY), "1000");
  }, [fetchDashboardData]);

  const handleGo = async () => {
    if (activeTab === "dashboard") {
      fetchDashboardData(dashCompanyCode, dashFiscalYear, dashStatementVersion);
      return;
    }

    const current = tabStates[activeTab] || createDefaultTabState();
    if (!current.companyCode || !current.companyCode.trim()) {
      toast.error("Please select a Company Code");
      return;
    }
    if (!current.fiscalYear) {
      toast.error("Please select a Fiscal Year");
      return;
    }

    if (activeTab === "trial-balance") {
      if (!current.statementVersion) {
        toast.error("Please select a Statement Version");
        return;
      }
      if (!current.ledger) {
        toast.error("Please select a Ledger (e.g. 0L)");
        return;
      }
      if (!current.tbFromDate || !current.tbToDate) {
        toast.error("Please select From Date and To Date");
        return;
      }
      if (current.tbFromDate > current.tbToDate) {
        toast.error("From Date cannot be later than To Date");
        return;
      }
      updateCurrentTab({ loading: true });
      try {
        const getPeriodFromDate = (dateStr) => {
          if (!dateStr) return 12;
          const parts = dateStr.split("-");
          const month = parseInt(parts[1], 10) || 4;
          return ((month - 4 + 12) % 12) + 1;
        };
        const periodNum = getPeriodFromDate(current.tbToDate);
        const hasComp = Boolean(current.comparisonPeriodNumber && current.comparisonPeriodNumber !== "none");
        const compYr = hasComp ? (current.comparisonYear ? Number(current.comparisonYear) : Number(current.fiscalYear) - 1) : undefined;
        const compPer = hasComp ? Number(current.comparisonPeriodNumber) : undefined;
        const res = await axios.get(`${API}/financial-agent/trial-balance`, {
          params: {
            fiscal_year: Number(current.fiscalYear),
            period_number: periodNum,
            company_code: current.companyCode.trim(),
            ledger: current.ledger,
            from_date: current.tbFromDate,
            to_date: current.tbToDate,
            statement_version: current.statementVersion || "1000",
            statement_type: current.statementType,
            comparison_year: compYr,
            comparison_period: compPer,
            profit_center: current.profitCenter.trim() || undefined,
            include_profit_center: current.includeProfitCenter,
            segment: current.segment.trim() || undefined,
            currency_type: current.currencyType,
          },
        });
        updateCurrentTab({ data: res.data, loading: false });
      } catch (e) {
        console.error(e);
        toast.error("Failed to load S/4HANA Trial Balance");
        updateCurrentTab({ loading: false });
      }
    } else {
      // Income Statement, Balance Sheet, Cash Flow
      updateCurrentTab({ loading: true });
      try {
        const hasComp = Boolean(current.comparisonPeriodNumber && current.comparisonPeriodNumber !== "none");
        const compYr = hasComp ? (current.comparisonYear ? Number(current.comparisonYear) : Number(current.fiscalYear) - 1) : null;

        const [mainRes, compRes] = await Promise.all([
          axios.get(`${API}/financial-agent/data`, {
            params: {
              fiscal_year: Number(current.fiscalYear),
              period_type: current.periodType || "monthly",
              company_code: current.companyCode.trim(),
              statement_version: current.statementVersion || undefined,
              ledger: current.ledger || "0L",
              from_date: current.fromDate || undefined,
              to_date: current.toDate || undefined,
            },
          }),
          compYr
            ? axios
                .get(`${API}/financial-agent/data`, {
                  params: {
                    fiscal_year: compYr,
                    period_type: current.periodType || "monthly",
                    company_code: current.companyCode.trim(),
                    statement_version: current.statementVersion || undefined,
                    ledger: current.ledger || "0L",
                  },
                })
                .catch(() => null)
            : Promise.resolve(null),
        ]);

        updateCurrentTab({
          data: mainRes.data,
          compData: compRes?.data || null,
          loading: false,
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to load financial statement data");
        updateCurrentTab({ loading: false });
      }
    }
  };

  const handleResetAll = () => {
    setTabStates((prev) => ({
      ...prev,
      [activeTab]: createDefaultTabState(),
    }));
  };

  const handleExport = async (reportType) => {
    const targetCode = activeTab === "dashboard" ? dashCompanyCode : currentTabState.companyCode;
    const targetFY = activeTab === "dashboard" ? dashFiscalYear : currentTabState.fiscalYear;
    if (!targetCode || !targetCode.trim()) {
      toast.error("Please select a Company Code before exporting");
      return;
    }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/financial-agent/export/${reportType}`, {
        params: {
          fiscal_year: targetFY || currentTabState.fiscalYear,
          period_type: currentTabState.periodType || "monthly",
          company_code: targetCode.trim(),
          ledger: currentTabState.ledger || "0L",
          from_date: currentTabState.fromDate,
          to_date: currentTabState.toDate,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `financial_${reportType}_${targetFY || "report"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loadingCodes) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const selectedCodeInfo = companyCodes.find(
    (c) => c.company_code === (activeTab === "dashboard" ? dashCompanyCode : currentTabState.companyCode)
  );
  const currentActiveData = activeTab === "dashboard" ? dashData : currentTabState.data;
  const activeDataSource = currentActiveData?.data_source;
  const activeMessage = currentActiveData?.message;

  return (
    <div className="space-y-6" data-testid="financial-analyst-page">
      {activeTab !== "daily-collections" && <DataSourceNotice source={activeDataSource} message={activeMessage} />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Landmark className="w-6 h-6 text-primary" />
            Financial Analyst
            {activeTab !== "daily-collections" && <Badge
              variant="secondary"
              className={`gap-1 text-xs ml-1 ${
                activeDataSource === "sap_live"
                  ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                  : "text-amber-700 bg-amber-50 border-amber-200"
              }`}
              data-testid="fa-sap-live-badge"
            >
              <Database className="w-3.5 h-3.5" />
              {activeDataSource === "sap_live"
                ? "SAP Live"
                : "Sample Data"}
            </Badge>}
          </h1>
          <p className="text-sm text-muted-foreground">
            Financial performance KPIs, statements, and AI-powered analysis
            {selectedCodeInfo && (
              <span className="ml-1 text-xs">
                - {selectedCodeInfo.name} ({selectedCodeInfo.currency})
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="inline-flex h-auto items-center justify-start gap-1.5 p-1.5 w-auto max-w-full overflow-x-auto" data-testid="financial-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="px-3.5 py-1.5">Dashboard</TabsTrigger>
          <TabsTrigger value="daily-collections" data-testid="tab-daily-collections" className="px-3.5 py-1.5">Daily Collections</TabsTrigger>
          <TabsTrigger value="income" data-testid="tab-income" className="px-3.5 py-1.5">Income Statement</TabsTrigger>
          <TabsTrigger value="balance" data-testid="tab-balance" className="px-3.5 py-1.5">Balance Sheet</TabsTrigger>
          <TabsTrigger value="trial-balance" data-testid="tab-trial-balance" className="px-3.5 py-1.5">Trial Balance</TabsTrigger>
          <TabsTrigger value="cashflow" data-testid="tab-cashflow" className="px-3.5 py-1.5">Cash Flow</TabsTrigger>
          <TabsTrigger value="analyst" data-testid="tab-analyst" className="px-3.5 py-1.5">AI Analyst</TabsTrigger>
        </TabsList>

        {/* SINGLE UNIFIED FILTER CONTAINER - Hidden on AI Analyst tab */}
        {!["analyst", "daily-collections"].includes(activeTab) && (
          <div className="flex flex-wrap items-center justify-between gap-2.5 p-3 bg-card rounded-lg border shadow-xs mt-4" data-testid="financial-filter-toolbar">
            {activeTab === "dashboard" ? (
              /* CLEAN DASHBOARD HEADER STRIP (Defaults for Executive KPI View) */
              <>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <Badge variant="outline" className="gap-1.5 py-1 px-2.5 bg-muted/30 border-muted-foreground/20 font-medium">
                    <Building2 className="w-3.5 h-3.5 text-primary" />
                    <span className="font-bold text-foreground">{dashCompanyCode || "1000"}</span>
                    {selectedCodeInfo && <span className="text-muted-foreground">({selectedCodeInfo.name})</span>}
                  </Badge>
                  <Badge variant="outline" className="gap-1.5 py-1 px-2.5 bg-muted/30 border-muted-foreground/20 font-medium">
                    <Layers className="w-3.5 h-3.5 text-primary" />
                    <span>FSV: <strong className="text-foreground">{dashStatementVersion || "1000"}</strong></span>
                  </Badge>
                  <Badge variant="outline" className="gap-1.5 py-1 px-2.5 bg-muted/30 border-muted-foreground/20 font-medium">
                    <Calendar className="w-3.5 h-3.5 text-primary" />
                    <span>FY <strong className="text-foreground">{dashFiscalYear || defaultFY}-{String(Number(dashFiscalYear || defaultFY) + 1).slice(2)}</strong></span>
                  </Badge>
                </div>

                <div className="flex items-center gap-2 ml-auto">
                  {/* Filter Criteria Button (Popup Modal) */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1.5 border-primary/30 text-primary hover:bg-primary/5 font-semibold shrink-0"
                    onClick={() => setIsFilterModalOpen(true)}
                    data-testid="filter-popup-btn"
                  >
                    <Filter className="w-3.5 h-3.5 text-primary" />
                    Filters
                    <Badge variant="secondary" className="h-4 px-1.5 text-[10px] ml-0.5 font-bold">
                      {(dashCompanyCode ? 1 : 0) + (dashFiscalYear ? 1 : 0) + (dashStatementVersion ? 1 : 0)}
                    </Badge>
                  </Button>
                </div>
              </>
            ) : (
              /* STATEMENT / TRIAL BALANCE INTERACTIVE TOOLBAR (Empty by default) */
              <div className="flex flex-wrap items-end gap-2.5 w-full">
                {/* 1. Company */}
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-muted-foreground">Company</label>
                  {companyCodes.length > 0 ? (
                    <Select value={currentTabState.companyCode} onValueChange={handleCompanyChange}>
                      <SelectTrigger className="w-[180px] h-9 text-xs" data-testid="company-code-select">
                        <SelectValue placeholder="Select Company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companyCodes.map((cc) => (
                          <SelectItem key={cc.company_code} value={cc.company_code}>
                            {cc.company_code} - {cc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      data-testid="company-code-input"
                      className="w-[120px] h-9 text-xs"
                      placeholder="e.g. 1000"
                      value={currentTabState.companyCode}
                      onChange={(e) => handleCompanyChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleGo();
                      }}
                    />
                  )}
                </div>

                {/* 2. Statement Version (FSV) */}
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-muted-foreground flex items-center gap-1">
                    <Layers className="w-3 h-3 text-primary" />
                    Statement Version
                  </label>
                  <Select value={currentTabState.statementVersion} onValueChange={(val) => updateCurrentTab({ statementVersion: val })} disabled={!currentTabState.companyCode}>
                    <SelectTrigger className="w-[260px] h-9 text-xs" data-testid="statement-version-select">
                      <SelectValue placeholder={currentTabState.companyCode ? "Select Statement Version" : "Select Company first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {statementVersions.map((v) => (
                        <SelectItem key={v.version} value={v.version}>
                          <span className="font-semibold mr-1">{v.version}</span> - {v.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* 3. Fiscal Year */}
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-muted-foreground">Fiscal Year</label>
                  <Select value={currentTabState.fiscalYear} onValueChange={handleFiscalYearChange}>
                    <SelectTrigger className="w-[125px] h-9 text-xs" data-testid="fiscal-year-select">
                      <SelectValue placeholder="Select Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {fyOptions.map((y) => (
                        <SelectItem key={y} value={String(y)}>FY {y}-{String(y + 1).slice(2)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* 4. Ledger */}
                <div className="w-[120px] space-y-1">
                  <label className="text-[11px] font-medium text-muted-foreground">Ledger</label>
                  <Select value={currentTabState.ledger || "0L"} onValueChange={(val) => updateCurrentTab({ ledger: val })}>
                    <SelectTrigger className="h-9 text-xs" data-testid="statement-ledger-select">
                      <SelectValue placeholder="0L (Leading)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0L">0L (Leading)</SelectItem>
                      <SelectItem value="2L">2L (Group)</SelectItem>
                      <SelectItem value="3L">3L (Tax)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {activeTab !== "trial-balance" && (
                  <>
                    {/* End Period */}
                    <div className="w-[175px] space-y-1">
                      <label className="text-[11px] font-medium text-muted-foreground">End Period</label>
                      <Select value={currentTabState.periodNumber || "all"} onValueChange={handlePeriodChange}>
                        <SelectTrigger className="h-9 text-xs" data-testid="statement-period-select">
                          <SelectValue placeholder="All Periods (Full Year)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            <span className="text-muted-foreground italic">All Periods (Full Year)</span>
                          </SelectItem>
                          {PERIODS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              Period {p.label} {currentTabState.fiscalYear ? `(${currentTabState.fiscalYear})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Comparison End Period */}
                    <div className="w-[185px] space-y-1">
                      <label className="text-[11px] font-medium text-muted-foreground">Comparison End Period</label>
                      <Select value={currentTabState.comparisonPeriodNumber || "none"} onValueChange={handleCompPeriodChange}>
                        <SelectTrigger className="h-9 text-xs" data-testid="statement-comp-period-select">
                          <SelectValue placeholder="No Comparison" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="text-muted-foreground italic">None (Standard View)</span>
                          </SelectItem>
                          {PERIODS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              Period {p.label} {currentTabState.comparisonYear ? `(${currentTabState.comparisonYear})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {activeTab === "trial-balance" && (
                  <>
                    {/* From Date */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-muted-foreground">From Date</label>
                      <Input
                        type="date"
                        className="w-[150px] h-9 text-xs px-2.5"
                        value={currentTabState.tbFromDate}
                        onChange={(e) => updateCurrentTab({ tbFromDate: e.target.value })}
                        data-testid="tb-from-date-input"
                      />
                    </div>

                    {/* To Date */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-muted-foreground">To Date</label>
                      <Input
                        type="date"
                        className="w-[150px] h-9 text-xs px-2.5"
                        value={currentTabState.tbToDate}
                        onChange={(e) => updateCurrentTab({ tbToDate: e.target.value })}
                        data-testid="tb-to-date-input"
                      />
                    </div>

                    {/* Comparison End Period */}
                    <div className="w-[185px] space-y-1">
                      <label className="text-[11px] font-medium text-muted-foreground">Comparison End Period</label>
                      <Select value={currentTabState.comparisonPeriodNumber || "none"} onValueChange={handleCompPeriodChange}>
                        <SelectTrigger className="h-9 text-xs" data-testid="tb-comp-period-select">
                          <SelectValue placeholder="No Comparison" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="text-muted-foreground italic">None (Standard View)</span>
                          </SelectItem>
                          {PERIODS.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              Period {p.label} {currentTabState.comparisonYear ? `(${currentTabState.comparisonYear})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {activeTab === "trial-balance" && (
                  /* View Layout Toggle */
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground">View Layout</label>
                    <div className="flex items-center rounded-md border bg-muted/40 p-0.5 h-9">
                      <button
                        type="button"
                        onClick={() => handleViewModeChange("comparison")}
                        className={`px-2.5 py-1 text-xs rounded font-medium transition-all ${
                          currentTabState.viewMode === "comparison"
                            ? "bg-background text-foreground shadow-xs font-semibold"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid="tb-view-comparison"
                      >
                        S/4HANA Variance
                      </button>
                      <button
                        type="button"
                        onClick={() => handleViewModeChange("standard")}
                        className={`px-2.5 py-1 text-xs rounded font-medium transition-all ${
                          currentTabState.viewMode === "standard"
                            ? "bg-background text-foreground shadow-xs font-semibold"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid="tb-view-standard"
                      >
                        Standard TB
                      </button>
                    </div>
                  </div>
                )}

                {/* More Filters Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 text-xs gap-1.5 shrink-0"
                  onClick={() => setIsAdaptFiltersOpen(true)}
                  data-testid="tb-adapt-filters-btn"
                >
                  <Filter className="w-3.5 h-3.5 text-primary" />
                  More Filters
                  {activeAdaptFilterCount > 0 && (
                    <Badge variant="default" className="h-4.5 px-1.5 text-[10px] ml-0.5">
                      {activeAdaptFilterCount}
                    </Badge>
                  )}
                </Button>

                {/* SINGLE GO BUTTON */}
                <Button
                  size="sm"
                  className="h-9 w-[68px] shrink-0 justify-center text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                  onClick={handleGo}
                  disabled={currentTabState.loading}
                  data-testid="header-go-btn"
                >
                  {currentTabState.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                  GO
                </Button>

                {/* Export All */}
                <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => handleExport("all")} disabled={exporting} data-testid="export-all-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                  Export All
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Tab Contents */}
        <TabsContent value="dashboard" className="mt-4">
          {dashLoading ? (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-card">
              <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
              <span className="text-sm text-muted-foreground">Loading Executive Dashboard KPIs...</span>
            </div>
          ) : !dashData ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-card rounded-lg border shadow-sm">
              <Building2 className="w-10 h-10 text-muted-foreground/50 mb-3" />
              <h3 className="text-base font-semibold">Select Filter Criteria</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Please click <strong>Filters</strong> above to customize criteria and view KPIs.
              </p>
            </div>
          ) : (
            <KPIDashboard kpis={dashData?.kpis} />
          )}
        </TabsContent>

        <TabsContent value="daily-collections" className="mt-4">
          <DailyCollections companyCodes={companyCodes} />
        </TabsContent>

        <TabsContent value="income" className="mt-4">
          {tabStates.income.loading ? (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-card">
              <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
              <span className="text-sm text-muted-foreground">Loading Income Statement...</span>
            </div>
          ) : !tabStates.income.data ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-card rounded-lg border shadow-sm" data-testid="income-empty-prompt">
              <Building2 className="w-10 h-10 text-muted-foreground/50 mb-3" />
              <h3 className="text-base font-semibold">Selection Criteria Required</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Please select your <strong>Company</strong> and <strong>Fiscal Year</strong> above, then click <strong>GO</strong> to view Income Statement.
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => handleExport("income_statement")} disabled={exporting} data-testid="export-income-btn">
                  <Download className="w-4 h-4 mr-1" /> Export P&L
                </Button>
              </div>
              <IncomeStatement
                data={tabStates.income.data?.income_statement}
                endPeriod={tabStates.income.periodNumber}
                comparisonData={tabStates.income.compData?.income_statement}
                comparisonPeriod={tabStates.income.comparisonPeriodNumber}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="balance" className="mt-4">
          {tabStates.balance.loading ? (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-card">
              <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
              <span className="text-sm text-muted-foreground">Loading Balance Sheet...</span>
            </div>
          ) : !tabStates.balance.data ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-card rounded-lg border shadow-sm" data-testid="balance-empty-prompt">
              <Building2 className="w-10 h-10 text-muted-foreground/50 mb-3" />
              <h3 className="text-base font-semibold">Selection Criteria Required</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Please select your <strong>Company</strong> and <strong>Fiscal Year</strong> above, then click <strong>GO</strong> to view Balance Sheet.
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => handleExport("balance_sheet")} disabled={exporting} data-testid="export-balance-btn">
                  <Download className="w-4 h-4 mr-1" /> Export B/S
                </Button>
              </div>
              <BalanceSheet
                data={tabStates.balance.data?.balance_sheet}
                endPeriod={tabStates.balance.periodNumber}
                comparisonData={tabStates.balance.compData?.balance_sheet}
                comparisonPeriod={tabStates.balance.comparisonPeriodNumber}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="trial-balance" className="mt-4">
          <TrialBalance
            data={tabStates["trial-balance"].data}
            loading={tabStates["trial-balance"].loading}
            viewMode={tabStates["trial-balance"].viewMode}
            companyCode={tabStates["trial-balance"].companyCode}
            fiscalYear={tabStates["trial-balance"].fiscalYear ? Number(tabStates["trial-balance"].fiscalYear) : null}
            statementVersion={tabStates["trial-balance"].statementVersion}
            statementVersions={statementVersions}
            ledger={tabStates["trial-balance"].ledger}
            fromDate={tabStates["trial-balance"].tbFromDate}
            toDate={tabStates["trial-balance"].tbToDate}
            periodNumber={tabStates["trial-balance"].periodNumber}
            comparisonPeriodNumber={tabStates["trial-balance"].comparisonPeriodNumber}
            comparisonYear={tabStates["trial-balance"].comparisonYear}
            profitCenter={tabStates["trial-balance"].profitCenter}
            segment={tabStates["trial-balance"].segment}
            onReset={handleResetAll}
            onRefresh={handleGo}
          />
        </TabsContent>

        <TabsContent value="cashflow" className="mt-4">
          {tabStates.cashflow.loading ? (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-card">
              <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
              <span className="text-sm text-muted-foreground">Loading Cash Flow Forecast...</span>
            </div>
          ) : !tabStates.cashflow.data ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-card rounded-lg border shadow-sm" data-testid="cashflow-empty-prompt">
              <Building2 className="w-10 h-10 text-muted-foreground/50 mb-3" />
              <h3 className="text-base font-semibold">Selection Criteria Required</h3>
              <p className="text-sm text-muted-foreground max-w-md mt-1">
                Please select your <strong>Company</strong> and <strong>Fiscal Year</strong> above, then click <strong>GO</strong> to view Cash Flow.
              </p>
            </div>
          ) : (
            <>
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => handleExport("cash_flow")} disabled={exporting} data-testid="export-cashflow-btn">
                  <Download className="w-4 h-4 mr-1" /> Export Cash Flow
                </Button>
              </div>
              <CashFlowForecast
                cashFlow={tabStates.cashflow.data?.cash_flow}
                forecast={tabStates.cashflow.data?.liquidity_forecast}
                endPeriod={tabStates.cashflow.periodNumber}
                comparisonData={tabStates.cashflow.compData?.cash_flow}
                comparisonPeriod={tabStates.cashflow.comparisonPeriodNumber}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="analyst" className="mt-4">
          <FinancialChat
            companyCode={currentTabState.companyCode || dashCompanyCode}
            fiscalYear={currentTabState.fiscalYear || dashFiscalYear}
            ledger={currentTabState.ledger || "0L"}
            fromDate={currentTabState.fromDate}
            toDate={currentTabState.toDate}
          />
        </TabsContent>
      </Tabs>

      {/* S/4HANA More Filters Modal */}
      <Dialog open={isAdaptFiltersOpen} onOpenChange={setIsAdaptFiltersOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-primary" />
              More Filters
            </DialogTitle>
            <DialogDescription>
              Configure additional criteria for your Financial Statement and Trial Balance.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Ledger */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">Ledger</label>
              <Select value={currentTabState.ledger || "0L"} onValueChange={(val) => updateCurrentTab({ ledger: val })}>
                <SelectTrigger className="h-9 text-xs" data-testid="adapt-ledger-select">
                  <SelectValue placeholder="0L (Leading)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0L">0L (Leading)</SelectItem>
                  <SelectItem value="2L">2L (Group)</SelectItem>
                  <SelectItem value="3L">3L (Tax)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Statement Type */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">Statement Type</label>
              <Select value={currentTabState.statementType} onValueChange={(val) => updateCurrentTab({ statementType: val })}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal (Actual - Actual)</SelectItem>
                  <SelectItem value="plan_actual">Plan - Actual (ACDOCP)</SelectItem>
                  <SelectItem value="forecast">Actual - Forecast</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Currency Mode */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold">Reporting Currency</label>
              <Select value={currentTabState.currencyType} onValueChange={(val) => updateCurrentTab({ currencyType: val })}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">INR - Company Code Currency</SelectItem>
                  <SelectItem value="global">USD - Global / Group Currency</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Profit Center Checkbox & Dropdown */}
            <div className="space-y-2 pt-1 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include-pc"
                  checked={currentTabState.includeProfitCenter}
                  onCheckedChange={(checked) => updateCurrentTab({ includeProfitCenter: Boolean(checked) })}
                />
                <label
                  htmlFor="include-pc"
                  className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Including Profit Center
                </label>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Profit Center</label>
                <Select value={currentTabState.profitCenter || "all"} onValueChange={(val) => updateCurrentTab({ profitCenter: val === "all" ? "" : val })}>
                  <SelectTrigger className="h-9 text-xs" data-testid="profit-center-select">
                    <SelectValue placeholder="All Profit Centers (Company Wide)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="text-muted-foreground italic">All Profit Centers (Company Wide)</span>
                    </SelectItem>
                    {profitCenters.map((pc) => (
                      <SelectItem key={pc.profit_center} value={pc.profit_center}>
                        <span className="font-semibold mr-1">{pc.profit_center}</span> - {pc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Segment */}
            <div className="space-y-1.5 pt-1 border-t">
              <label className="text-xs font-semibold">Segment (IFRS 8 / Ind AS 108)</label>
              <Select value={currentTabState.segment || "all"} onValueChange={(val) => updateCurrentTab({ segment: val === "all" ? "" : val })}>
                <SelectTrigger className="h-9 text-xs" data-testid="segment-select">
                  <SelectValue placeholder="All Segments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="text-muted-foreground italic">All Segments</span>
                  </SelectItem>
                  {segments.map((seg) => (
                    <SelectItem key={seg.segment} value={seg.segment}>
                      <span className="font-semibold mr-1">{seg.segment}</span> - {seg.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                updateCurrentTab({
                  ledger: "0L",
                  profitCenter: "",
                  includeProfitCenter: false,
                  segment: "",
                  currencyType: "local",
                  statementType: "normal",
                });
              }}
            >
              Reset Filters
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                setIsAdaptFiltersOpen(false);
                handleGo();
              }}
            >
              <Check className="w-4 h-4 mr-1" /> Apply Filters
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dashboard Filter Criteria Popup Modal */}
      <Dialog open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <Filter className="w-5 h-5 text-primary" />
              Dashboard KPI Filters
            </DialogTitle>
            <DialogDescription>
              Select Company and Fiscal Year to customize your Executive Dashboard KPIs.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-primary" />
                Company Code
              </label>
              {companyCodes.length > 0 ? (
                <Select value={dashCompanyCode} onValueChange={setDashCompanyCode}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Select Company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companyCodes.map((cc) => (
                      <SelectItem key={cc.company_code} value={cc.company_code}>
                        {cc.company_code} - {cc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  className="h-9 text-xs"
                  placeholder="e.g. 1000"
                  value={dashCompanyCode}
                  onChange={(e) => setDashCompanyCode(e.target.value)}
                />
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-primary" />
                Fiscal Year
              </label>
              <Select value={dashFiscalYear} onValueChange={setDashFiscalYear}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {fyOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>FY {y}-{String(y + 1).slice(2)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-between sm:justify-between pt-2 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setDashCompanyCode("1000");
                setDashFiscalYear(String(defaultFY));
                setDashStatementVersion("1000");
              }}
            >
              Reset to Defaults
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              onClick={() => {
                setIsFilterModalOpen(false);
                fetchDashboardData(dashCompanyCode, dashFiscalYear, dashStatementVersion);
              }}
            >
              <Check className="w-4 h-4 mr-1" /> Apply & Update Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialAnalyst;

